import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { UserFeedbackPayload, UserFeedbackResult } from './types';
import { v4 as uuid } from 'uuid';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { userFeedbackCreateBody } from '@/infrastructure/api/v1/mappers/userFeedback';

export class UserFeedbackService {
  constructor(private services: ReturnType<typeof useServices>) {}

  // The feedback is the signed-in user's; the server takes them from the session and starts responded_to as false
  async submitUserFeedback(payload: UserFeedbackPayload): Promise<UserFeedbackResult> {
    try {
      const row = {
        user_feedback_id: uuid(),
        source: payload.source,
        rating: payload.rating,
        question: payload.question ?? null,
        response: payload.text ?? null
      };

      const { error } = await toResult(v1.POST('/api/v1/user-feedback', { body: userFeedbackCreateBody(row) }));

      if (error) {
        console.error('Error submitting user feedback:', error);
        return {
          success: false,
          message: error.message ?? 'Failed to submit feedback'
        };
      }

      this.services.analytics.trackLureoUserFeedbackSubmitted({
        rating: payload.rating,
        source: payload.source,
        has_response: !!payload.text?.trim(),
      });

      return {
        success: true,
        message: 'Thanks for your feedback!'
      };
    } catch (err) {
      console.error('Error submitting user feedback:', err);
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Failed to submit feedback'
      };
    }
  }

  /**
   * Returns true if the user has previously submitted any feedback (any source).
   * Used to avoid prompting again for in-editor or on-exit feedback.
   */
  async hasUserSubmittedFeedback(userId: string): Promise<boolean> {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/user-feedback', {
        params: { query: { userUniqueId: userId, limit: 1 } },
      }));

      if (error) {
        console.error('Error checking user feedback:', error);
        return false;
      }
      return data.items.length > 0;
    } catch (err) {
      console.error('Error checking user feedback:', err);
      return false;
    }
  }
}
